<template>
  <div class="uk-modal-dialog uk-modal-body uk-margin-auto-vertical form">
    <Form @submit="onBeforeSubmit" :validation-schema="validationSchema" ref="form" :initial-values="formValues">
      <div class="uk-modal-header form-header">
        <span class="uk-text-bold uk-text-large">{{ client && client.id ? "クライアント編集" : "クライアント作成" }}</span>
      </div>
      <div class="uk-modal-body">
        <FormError :errors="globalErrors" />
        <div class="field">
          <Field name="name" v-slot="{ field }">
            <FieldLabel title="クライアント名" :required="true" />
            <input class="uk-input" type="text" v-bind="field" />
          </Field>
          <FieldError name="name" />
        </div>
        <div class="price-plans">
          <FieldLabel title="料金プラン" />
          <div class="field-item">
            <Field
              v-for="pricePlan in availablePricePlans"
              :key="pricePlan.code"
              v-slot="{ field }"
              name="pricePlan"
              type="radio"
              :value="pricePlan.code"
            >
              <label>
                <input type="radio" :name="pricePlan" v-bind="field" :value="pricePlan.code" @change="onPricePlanChange" />
                {{ pricePlan.name }}
              </label>
            </Field>
            <div class="input-max-employees" v-show="displayMaxEmployee">
              <Field name="max_employees" v-slot="{ field }" v-show="displayMaxEmployee">
                <FieldLabel title="" :required="false" />
                <input class="uk-input max-employees" type="text" v-bind="field" />人まで
              </Field>
              <FieldError name="max_employees" />
            </div>
          </div>
        </div>
        <div class="service-plans">
          <FieldLabel title="サービスプラン" />
          <Field
            v-for="servicePlan in availableServicePlans"
            :key="servicePlan.code"
            v-slot="{ field }"
            :name="servicePlan.code"
            type="checkbox"
            :value="true"
          >
            <label>
              <input type="checkbox" :name="servicePlan.code" v-bind="field" :value="true" />
              {{ servicePlan.name }}
            </label>
          </Field>
        </div>
        <div class="uk-flex uk-flex-between" v-if="planStart">
          <div><span class="legend">開始 :</span> {{ planStart }}</div>
          <div v-if="planEnd"><span class="legend">終了 :</span> {{ planEnd }}</div>
        </div>
        <div class="uk-alert-primary" uk-alert v-if="!!message">
          <p>{{ message }}</p>
        </div>
      </div>
      <div class="uk-modal-footer uk-text-right form-action">
        <div>
          <ActionButton title="戻る" class="uk-modal-close" toggleTarget=".client-form-modal" />
        </div>
        <div class="form-action-main">
          <ActionButton title="完了" color="primary" type="submit" :disabled="processing" />
          <ActionButton v-if="client" title="削除" @click="onDeleteButtonClick" color="warn" />
        </div>
      </div>
    </Form>
  </div>
</template>
<style lang="scss" scoped>
@include modal-form;
.service-plans {
  display: flex;
  flex-direction: column;
  margin-bottom: $spacing-ex-small;
}
.price-plans {
  margin-bottom: $spacing-medium;
}
.price-plans input[type="radio"] {
  margin-top: $spacing-ex-small;
  margin-bottom: $spacing-ex-small;
}
input[type="checkbox"] {
  margin-right: $spacing-ex-small;
  margin-top: $spacing-ex-small;
}
input.max-employees {
  margin-left: $spacing-medium;
  width: $spacing-ex-medium;
}
.input-max-employees {
  display: inline-block;
}
span.legend {
  color: $brand-color;
  font-weight: bold;
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import { Prop } from "vue-property-decorator";
import FormMixin from "../../helpers/mixins/form";
import {
  ClientApi,
  Client,
  PricePlanEnum,
  GetServicePlan,
  GetPricePlan,
  SystemApi,
  ServicePlanEnum,
  ClientScheduledChangeEnum
} from "../../autogen";
import { Field, Form } from "vee-validate";
import FieldLabel from "../atoms/FieldLabel.vue";
import * as yup from "yup";
import FieldError from "../atoms/FieldError.vue";
import FormError from "../atoms/FormError.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
type FormValues = {
  name: string;
  pricePlan: PricePlanEnum;
  max_employees: string;
};
const ServicePlans = Object.values(ServicePlanEnum);
@Options({
  components: { Field, Form, FieldLabel, FieldError, ActionButton, FormError }
})
export default class ClientForm extends FormMixin {
  @Prop({ type: Object }) client?: Client;
  availableServicePlans: GetServicePlan[] = [];
  availablePricePlans: GetPricePlan[] = [];
  formName = "クライアント";
  defaultMaxEmployees = "3";
  formValues: FormValues = {
    name: "",
    pricePlan: PricePlanEnum.Trial,
    max_employees: ""
  };
  currentPricePlan = "";
  displayMaxEmployee = false;
  validationSchema!: yup.AnyObjectSchema;
  message = "";
  beforeCreate() {
    this.validationSchema = yup.object({
      name: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "クライアント名" }))
    });
  }
  get planStart() {
    let date = null;
    if (this.client?.planStart) date = new Date(this.client.planStart);
    else if (this.client?.createdAt) date = new Date(this.client.createdAt);
    return date ? `${date.getFullYear()}年${date.getMonth() + 1}月` : "";
  }
  get planEnd() {
    const date = this.client?.planEnd && new Date(this.client.planEnd);
    return date ? `${date.getFullYear()}年${date.getMonth() + 1}月` : "";
  }
  async beforeMount() {
    const getServicePlansResponse = await new SystemApi().getServicePlans({});
    this.availableServicePlans = getServicePlansResponse.data;
    const getPricePlansResponse = await new SystemApi().getPricePlans({});
    this.availablePricePlans = getPricePlansResponse.data;
    this.formValues = this.client
      ? {
          name: this.client.name,
          pricePlan: this.client.pricePlan,
          max_employees: this.client.max_employees ? this.client.max_employees : "",
          ...Object.keys(ServicePlanEnum).reduce((prev, current) => {
            prev[current.toLowerCase()] = (this.client!.servicePlans || [])
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .includes((ServicePlanEnum as any)![current]);
            return prev;
          }, {} as { [key: string]: boolean })
        }
      : {
          name: "",
          pricePlan: PricePlanEnum.Trial,
          max_employees: "",
          ...Object.keys(ServicePlanEnum).reduce((prev, current) => {
            prev[current.toLowerCase()] = false;
            return prev;
          }, {} as { [key: string]: boolean })
        };
    this.currentPricePlan = this.formValues.pricePlan;
    this.displayMaxEmployee = this.formValues.pricePlan === PricePlanEnum.Trial ? true : false;
    if (this.client?.scheduledChanges && this.client?.scheduledChanges.length) {
      const scheduledChange = this.client.scheduledChanges[0];
      const date = new Date(scheduledChange.plannedDate as string);
      const dateStr = `${date.getFullYear()}年${date.getMonth() + 1}月`;
      if (scheduledChange.plannedAction === ClientScheduledChangeEnum.ChangePlan) {
        const plan = this.availablePricePlans.find(({ code }) => code === scheduledChange.targetPricePlan);
        const planName = plan ? plan.name : scheduledChange.targetPricePlan;
        this.message = `「${dateStr}に${planName}に変更予定です」`;
      } else if (scheduledChange.plannedAction === ClientScheduledChangeEnum.Delete) {
        this.message = `「${dateStr}に削除予定です」`;
      }
    }
  }
  async onBeforeSubmit(formValues: FormValues) {
    await this.onSubmit(async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const servicePlans = ServicePlans.filter(key => (formValues as any)[key]);
      let max_employees = "";
      if (formValues.pricePlan === PricePlanEnum.Trial) {
        if (this.client) {
          max_employees = formValues.max_employees;
        } else {
          max_employees = formValues.max_employees ? formValues.max_employees : this.defaultMaxEmployees;
        }
      }
      const submitValues = {
        name: formValues.name,
        servicePlans,
        pricePlan: formValues.pricePlan,
        max_employees: max_employees
      };
      if (this.client) {
        await new ClientApi().updateClient({
          clientId: this.client.id,
          updateClientRequest: submitValues
        });
      } else {
        await new ClientApi().createClient({
          createClientRequest: submitValues
        });
      }
      this.$emit("submitted");
    });
  }
  async onDeleteButtonClick() {
    this.onDelete(async () => {
      await new ClientApi().deleteClient({ clientId: this.client!.id! });
    });
  }
  onPricePlanChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.currentPricePlan = target.value;
    this.displayMaxEmployee = target.value === PricePlanEnum.Trial ? true : false;
  }
}
</script>
